import type { QueryClient } from "@tanstack/react-query";
import { generateKey } from "./utils";
import type { FacilityOverviewResponse, SystemStatusResponse } from "../data/types";

// Field names are camelCase, not the wire's snake_case — parseSseBuffer
// (sseClient.ts) already runs camelCaseObject on every event payload before
// this handler ever sees it, same as every other SSE/REST consumer in this
// codebase. Confirmed via live BE integration testing (JAA-1095): the
// previous snake_case field names here (facility_id/snapshot_timestamp/
// system_status) silently read as undefined at runtime, making the
// systemStatus setQueryData call a no-op — "System Status page values not
// changing" in practice, despite every unit test passing (the mocks built
// the wrong, snake_case shape too).
export interface TelemetryEventPayload {
  facilityId: number;
  systemStatus: SystemStatusResponse;
  overview: FacilityOverviewResponse;
  // UTC ISO-8601 timestamp (Z suffix) of the reading this frame computed
  // from — used for the out-of-order guard below (JAA-1095, resolves FE-2).
  snapshotTimestamp: string;
  // True for a frame replaying a buffered/queued backlog reading, false for
  // a live one — used to suppress visible flicker during backfill (JAA-1095,
  // resolves part of FE-3).
  backfilled: boolean;
  // Raw telemetry/health passthrough and control_mode also arrive on this
  // payload but have no identified consumer yet — PENDING FE-12 in the FE
  // push architecture spec (§05.1). Deliberately left untyped/unused here
  // rather than invented.
}

// useSystemStatus's REST endpoint is parameterized by a server-side status
// filter (all/online/offline/faulted/maintenance/standby), but the pushed
// event:telemetry payload is always a single unfiltered snapshot. Only the
// default "all" filter's cache entry is direct-patched — a facility page
// viewing a different filter won't receive live pushed updates for that
// filtered view until it switches back to "all" or remounts. This is an FE
// decision (accepted narrow gap), not a backend-confirmed behavior.
const DEFAULT_SYSTEM_STATUS_FILTER = "all";

/**
 * Builds a handler for a facility's event:telemetry pushes — direct
 * cache-patches systemStatus (default filter only, see above) and
 * facilityOverview, keyed on the closure-scoped facilityId (§05.1 of the FE
 * push architecture spec). No REST call is made either way.
 *
 * Two guards, both confirmed against the backend's local runbook (JAA-1095):
 *
 * - **Out-of-order guard (resolves FE-2):** BE's queue isn't strictly FIFO.
 *   A frame whose snapshot_timestamp isn't strictly newer than the last one
 *   actually applied is ignored — tracked per handler instance, i.e. per
 *   facility (a fresh handler is created on every facility switch, so this
 *   never leaks across facilities).
 * - **Backfilled suppression (resolves part of FE-3):** a frame replaying a
 *   buffered backlog (backfilled: true) is never patched into the cache at
 *   all, so a burst of them never flickers through stale/intermediate
 *   values — only the live (backfilled: false) frame that follows renders.
 *   Deliberately skipped *before* the ordering guard updates its
 *   last-applied timestamp, so a backfilled frame's timestamp can never
 *   block a genuinely live frame that happens to have an earlier one.
 */
export function createTelemetryPatchHandler(
  queryClient: QueryClient,
  facilityId: number,
) {
  let lastAppliedTimestampMs: number | null = null;

  return function handleTelemetryEvent(data: TelemetryEventPayload) {
    if (data.backfilled) return;

    const frameTimestampMs = Date.parse(data.snapshotTimestamp);
    if (lastAppliedTimestampMs !== null && frameTimestampMs <= lastAppliedTimestampMs) {
      return;
    }
    lastAppliedTimestampMs = frameTimestampMs;

    queryClient.setQueryData(
      generateKey("systemStatus", facilityId, {
        status: DEFAULT_SYSTEM_STATUS_FILTER,
      }),
      data.systemStatus,
    );
    queryClient.setQueryData(
      generateKey("facilityOverview", facilityId),
      data.overview,
    );
  };
}

const DIRECT_PATCH_ENTITIES = ["systemStatus", "facilityOverview"];

/**
 * Re-fetches systemStatus and facilityOverview for a facility — call this
 * whenever the facility's SSE connection reaches "live", including every
 * reconnect (not just the first connect), via useTelemetryStream's
 * onConnected. The stream never replays missed events, so without this, a
 * transient drop, the scheduled 30-minute token-TTL close (§4.3), or a tab
 * regaining visibility after being suspended would leave these
 * direct-patched pages silently stuck on stale data indefinitely — nothing
 * else re-triggers a refresh for them (JAA-1095; previously an unhandled
 * gap — onConnected existed but nothing called it).
 */
export function refetchDirectPatchEntitiesOnReconnect(
  queryClient: QueryClient,
  facilityId: number,
): void {
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[1] === facilityId &&
      DIRECT_PATCH_ENTITIES.includes(query.queryKey[0] as string),
  });
}
